/**
 * 區間版 v2 視圖模型（無 JSX）。
 *
 * 後端尚未提供的欄位會以 v1 資料估算，並記錄於 `mockFields` 供 UI 標記「模擬」。
 * 待 API 接上後，對應 mock 標記會自動消失。
 */

const ONE_DAY_MS = 86_400_000;
const TZ = "Asia/Taipei";

/** 後端偵測到用氣行為異常時附帶的提示（可選欄位，前端顯示確認卡） */
export interface BehaviorAlert {
  /** early = 用量比過去增加；late = 用量比過去減少；irregular = 規律性下降 */
  type: "early" | "late" | "irregular";
  /** 與歷史均值的差距（天），正 = 提早，負 = 延遲；optional */
  delta_days?: number | null;
}

export interface HomeGasStatusInterval {
  j_eligible?: boolean | null;
  /** 區間 v2：auto 客戶可顯示；問卷初估見 prediction_source */
  auto_eligible?: boolean | null;
  /** model | survey | none */
  prediction_source?: "model" | "survey" | "none" | null;
  prediction_status?: string | null;
  /** 用氣行為異常偵測（後端由 interval_cv / rolling_mean 計算後附帶） */
  behavior_alert?: BehaviorAlert | null;
  predicted_days?: number | null;
  last_order_date?: string | null;
  suggested_delivery_date?: string | null;
  /** API v2：耗完區間下界 */
  depletion_low?: string | null;
  /** API v2：耗完區間上界 */
  depletion_high?: string | null;
  delivery_earliest?: string | null;
  delivery_latest?: string | null;
  /** API v2：出車下界 L */
  dispatch_lower_date?: string | null;
  /** API v2：觀察上緣 H */
  observe_upper_date?: string | null;
  /** API v2：正式提醒 notify_standard */
  notify_standard_date?: string | null;
  notify_light_date?: string | null;
  notify_escalate_l3_date?: string | null;
  notify_escalate_l1_date?: string | null;
  /** API v2：auto | suggest | manual */
  notify_tier?: "auto" | "suggest" | "manual" | null;
  /** 叫桶間隔 CV；FE 鬆 G 顯示閘用 */
  interval_cv?: number | null;
  /** 滾動中位叫桶週期（天）；FE 鬆 G 顯示閘用 */
  rolling_median_interval_days?: number | null;
}

/** 目前以模擬值填充、待後端接線的欄位 */
export type MockFieldKey =
  | "depletion_interval"
  | "dispatch_lower_date"
  | "observe_upper_date"
  | "notify_tier"
  | "notify_stages"
  | "stock_status_derived";

/** 模擬欄位中文說明（工程對照見 mockFieldApiNames） */
export const MOCK_FIELD_LABELS: Record<MockFieldKey, string> = {
  depletion_interval: "預估用完區間",
  dispatch_lower_date: "建議出車日（L = q08，與 depletion_low 同天）",
  observe_upper_date: "觀察上緣",
  notify_tier: "通知策略層級",
  notify_stages: "提醒叫桶日程",
  stock_status_derived: "存量狀態（依出車日推算）",
};

/** 對應後端/API 欄位名，供工程查閱 */
export const MOCK_FIELD_API_NAMES: Record<MockFieldKey, string> = {
  depletion_interval: "depletion_low, depletion_high",
  dispatch_lower_date: "dispatch_lower_date",
  observe_upper_date: "observe_upper_date",
  notify_tier: "notify_tier",
  notify_stages: "notify_light_date, notify_standard_date, notify_escalate_l3_date, notify_escalate_l1_date",
  stock_status_derived: "（衍生）days_to_dispatch → stockStatus",
};

export type CustomerStockStatus = "充足" | "留意" | "偏低" | "建議補充";
export type StockPhase = "early" | "in_window" | "near_lower" | "urgent";
export type NotifyStageKey = "light" | "standard" | "escalate_l3" | "escalate_l1" | "dispatch";

/** 區間視覺：長週期用摘要、中週期用進度條、近窗期用聚焦時間軸 */
export type IntervalTimelineMode = "summary" | "progress" | "focus";

export interface NotifyStageItem {
  key: NotifyStageKey;
  date: string;
  label: string;
  customerVisible: boolean;
  tone: "muted" | "normal" | "strong" | "urgent";
  /** 此階段日期是否為前端估算 */
  isMock: boolean;
}

export interface IntervalGasViewModel {
  depletionLow: string | null;
  depletionHigh: string | null;
  dispatchByDate: string | null;
  observeUpperDate: string | null;
  lastOrderDate: string | null;
  stockStatus: CustomerStockStatus;
  phase: StockPhase;
  daysToDispatch: number | null;
  statusSubtitle: string;
  depletionRangeLabel: string;
  dispatchHint: string;
  /** 週期總天數（上次叫桶 → 預估用完上緣） */
  cycleSpanDays: number;
  /** 週期已過百分比 0–100 */
  cycleProgressPercent: number;
  /** 距預估用完窗下緣天數 */
  daysToDepletionWindow: number | null;
  intervalTimelineMode: IntervalTimelineMode;
  /** 區間卡片副標（長週期友善說明） */
  intervalCaption: string;
  showDispatchHint: boolean;
  /** 提醒三態：提醒日前 / 已到提醒日 / 已進入預估用完區間 */
  reminderStage: "before" | "due" | "in_window";
  /** Hero 一句話情境（依提醒三態切換文案） */
  heroContextLine: string;
  /** 時間軸「今天」標籤文字（含存量狀態，例：今天 · 充足） */
  todayStatusLabel: string;
  /** 進度條／聚焦軸起訖（依 mode 計算） */
  timelineRangeStart: string;
  timelineRangeEnd: string;
  notifyStages: NotifyStageItem[];
  activeReminder: {
    title: string;
    body: string;
    tone: "muted" | "normal" | "strong" | "urgent";
    icon: "bell" | "truck";
  } | null;
  showAutoDispatchMessage: boolean; // 暖啟動固定 false（見 HomePage ENABLE_AUTO_DISPATCH_UI）
  tankFillPercent: number;
  statusColor: string;
  heroGradient: string;
  /** 含模擬值的欄位鍵（空陣列 = 全部來自 API） */
  mockFields: MockFieldKey[];
  hasMockFields: boolean;
  depletionIntervalIsMock: boolean;
  dispatchHintIsMock: boolean;
  reminderIsMock: boolean;
}

export const toMonthDay = (date: string | null | undefined): string => {
  if (!date) return "--";
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[1]}/${parts[2]}`;
};

/** 同年度只顯示月日；跨年時主標仍用月日，年份另以小字提示 */
export const formatDepletionRange = (low: string, high: string): string =>
  `${toMonthDay(low)} ~ ${toMonthDay(high)}`;

/** 跨年時回傳「2026–2027」；同年回 null */
export const formatDepletionYearHint = (
  low: string | null | undefined,
  high: string | null | undefined,
): string | null => {
  if (!low || !high) return null;
  const y1 = low.split("-")[0];
  const y2 = high.split("-")[0];
  if (!y1 || !y2 || y1.length !== 4 || y2.length !== 4) return null;
  if (y1 === y2) return null;
  return `${y1}–${y2}`;
};

const humanizeMonthsAhead = (days: number): string => {
  if (days <= 0) return "已進入預估窗";
  if (days < 14) return `約 ${days} 天後`;
  if (days < 45) return `約 ${Math.round(days / 7)} 週後`;
  const months = Math.max(1, Math.round(days / 30));
  return months >= 12 ? `約 ${Math.round(months / 12)} 年後` : `約 ${months} 個月後`;
};

const humanizeCycleProgress = (percent: number): string => {
  if (percent < 8) return "週期剛開始";
  if (percent < 92) return `週期約 ${Math.round(percent / 10) * 10}%`;
  return "接近預估用完";
};

const pickTimelineMode = (
  cycleSpanDays: number,
  daysToWindow: number | null,
  phase: StockPhase,
): IntervalTimelineMode => {
  if (daysToWindow !== null && daysToWindow <= 45) return "focus";
  if (cycleSpanDays > 120 || (daysToWindow !== null && daysToWindow > 60)) return "summary";
  return "progress";
};

const buildIntervalCaption = (
  mode: IntervalTimelineMode,
  daysToWindow: number | null,
  cycleProgressPercent: number,
  phase: StockPhase,
): string => {
  if (phase === "urgent" || phase === "near_lower") return "已接近建議安排時間";
  if (phase === "in_window") return "目前落在預估用完窗內";

  if (mode === "summary" && daysToWindow !== null) {
    return `${humanizeMonthsAhead(daysToWindow)}進入預估窗 · ${humanizeCycleProgress(cycleProgressPercent)}`;
  }
  if (mode === "progress") {
    return humanizeCycleProgress(cycleProgressPercent);
  }
  return humanizeCycleProgress(cycleProgressPercent);
};

const todayIso = (): string => {
  const d = new Date();
  return d.toLocaleString("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });
};

export const addDaysToIsoDate = (iso: string, days: number): string | null => {
  const t = new Date(`${iso}T00:00:00+08:00`).getTime();
  if (Number.isNaN(t)) return null;
  const d = new Date(t + days * ONE_DAY_MS);
  const y = d.toLocaleString("en-CA", { timeZone: TZ, year: "numeric" });
  const m = d.toLocaleString("en-CA", { timeZone: TZ, month: "2-digit" });
  const day = d.toLocaleString("en-CA", { timeZone: TZ, day: "2-digit" });
  return `${y}-${m}-${day}`;
};

export const daysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(`${iso}T00:00:00+08:00`).getTime();
  if (Number.isNaN(t)) return null;
  const todayT = new Date(`${todayIso()}T00:00:00+08:00`).getTime();
  return Math.floor((t - todayT) / ONE_DAY_MS);
};

const daysBetween = (from: string, to: string): number | null => {
  const a = new Date(`${from}T00:00:00+08:00`).getTime();
  const b = new Date(`${to}T00:00:00+08:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / ONE_DAY_MS);
};

/* 充足＝藍綠安心；留意／偏低＝橘系升溫；建議補充＝紅色告急。
   tank 值決定液位高度；液體色由 GlassTank tone＝stockStatus 對齊 */
const STATUS_THEME: Record<
  CustomerStockStatus,
  { color: string; gradient: string; tank: number }
> = {
  充足: { color: "#16a34a", gradient: "linear-gradient(165deg, #10B981 0%, #06B6D4 60%, #3B82F6 100%)", tank: 90 },
  留意: { color: "#d97706", gradient: "linear-gradient(165deg, #FBBF24 0%, #F59E0B 50%, #F97316 100%)", tank: 50 },
  偏低: { color: "#ea580c", gradient: "linear-gradient(165deg, #FDBA74 0%, #FB923C 50%, #F97316 100%)", tank: 32 },
  建議補充: { color: "#dc2626", gradient: "linear-gradient(165deg, #F87171 0%, #EF4444 55%, #DC2626 100%)", tank: 10 },
};

const stockStatusFromDaysToDispatch = (days: number | null): CustomerStockStatus => {
  if (days === null) return "充足";
  if (days <= 0) return "建議補充";
  if (days <= 3) return "偏低";
  if (days <= 7) return "留意";
  return "充足";
};

const phaseFromDates = (dispatch: string | null, low: string | null, high: string | null): StockPhase => {
  const dDispatch = daysUntil(dispatch);
  const dLow = daysUntil(low);
  const dHigh = daysUntil(high);
  if (dDispatch !== null && dDispatch <= 0) return "urgent";
  if (dDispatch !== null && dDispatch <= 3) return "near_lower";
  if (dLow !== null && dHigh !== null && dLow <= 0 && dHigh >= 0) return "in_window";
  if (dLow !== null && dLow > 0) return "early";
  return "in_window";
};

const buildStatusSubtitle = (status: CustomerStockStatus, daysToDispatch: number | null): string => {
  if (daysToDispatch === null) return "持續觀察用量中";
  if (status === "建議補充") return "建議今天安排叫桶";
  if (status === "偏低") return `預計 ${daysToDispatch} 天內建議補充`;
  if (status === "留意") return `約 ${daysToDispatch} 天後進入建議窗`;
  return `距建議安排還有 ${daysToDispatch} 天`;
};

/** 客戶端提醒三態（階段梯不對客戶露出，僅留在 notifyStages 供後台/排程） */
export type ReminderStage = "before" | "due" | "in_window";

const pickActiveReminder = (
  stage: ReminderStage,
  notifyStandardDate: string | null,
  dispatch: string | null,
  tier: "auto" | "suggest" | "manual",
): IntervalGasViewModel["activeReminder"] => {
  if (tier === "auto") return null;

  if (stage === "in_window") {
    return { title: "建議立即預約", body: "瓦斯可能隨時用完，請儘快預約", tone: "urgent", icon: "truck" };
  }
  if (stage === "due") {
    return {
      title: "建議先預約",
      body: dispatch ? `建議最晚於 ${toMonthDay(dispatch)} 前安排配送` : "建議儘早預約叫桶",
      tone: "strong",
      icon: "bell",
    };
  }
  if (!notifyStandardDate) return null;
  return { title: "叫桶提醒", body: `${toMonthDay(notifyStandardDate)} 會提醒您`, tone: "normal", icon: "bell" };
};

export const buildIntervalGasViewModel = (
  gs: HomeGasStatusInterval | null | undefined,
): IntervalGasViewModel | null => {
  if (!gs?.last_order_date) return null;

  const mockFields: MockFieldKey[] = [];
  const cycle = Number(gs.predicted_days);
  const hasCycle = Number.isFinite(cycle) && cycle > 0;

  const hasDepletionFromApi = Boolean(
    (gs.depletion_low && gs.depletion_high) || (gs.delivery_earliest && gs.delivery_latest),
  );

  let depletionLow = gs.depletion_low ?? gs.delivery_earliest ?? null;
  let depletionHigh = gs.depletion_high ?? gs.delivery_latest ?? gs.observe_upper_date ?? null;

  if ((!depletionLow || !depletionHigh) && hasCycle) {
    depletionLow = addDaysToIsoDate(gs.last_order_date, Math.round(cycle * 0.88));
    depletionHigh = addDaysToIsoDate(gs.last_order_date, Math.round(cycle * 1.12));
    if (!hasDepletionFromApi) mockFields.push("depletion_interval");
  }

  // 建議配送日 = L（q08），與 depletion_low 同天；不再 fallback v1 suggested_delivery_date
  let dispatchByDate = gs.dispatch_lower_date ?? depletionLow;
  if (!gs.dispatch_lower_date && !gs.depletion_low && !gs.delivery_earliest) {
    mockFields.push("dispatch_lower_date");
  }

  let observeUpperDate = gs.observe_upper_date ?? depletionHigh;
  if (!gs.observe_upper_date && !gs.depletion_high && !gs.delivery_latest) {
    mockFields.push("observe_upper_date");
  }

  if (!depletionLow && dispatchByDate) depletionLow = dispatchByDate;
  if (!depletionHigh && observeUpperDate) depletionHigh = observeUpperDate;
  if (!depletionLow || !depletionHigh) return null;

  const tierFromApi = gs.notify_tier ?? null;
  const tier = tierFromApi ?? "suggest";
  if (!tierFromApi) mockFields.push("notify_tier");

  const dispatch = dispatchByDate;
  const mkStage = (
    key: NotifyStageKey,
    apiDate: string | null | undefined,
    fallbackDate: string | null,
    label: string,
    customerVisible: boolean,
    tone: NotifyStageItem["tone"],
  ): NotifyStageItem | null => {
    const date = apiDate ?? fallbackDate;
    if (!date) return null;
    const isMock = !apiDate;
    if (isMock && !mockFields.includes("notify_stages")) mockFields.push("notify_stages");
    return { key, date, label, customerVisible, tone, isMock };
  };

  const notifyStages = [
    mkStage(
      "light",
      gs.notify_light_date,
      dispatch ? addDaysToIsoDate(dispatch, -10) : null,
      "輕提醒",
      tier === "manual",
      "muted",
    ),
    mkStage(
      "standard",
      gs.notify_standard_date,
      dispatch ? addDaysToIsoDate(dispatch, -5) : null,
      "提醒叫桶",
      tier !== "auto",
      "normal",
    ),
    mkStage(
      "escalate_l3",
      gs.notify_escalate_l3_date,
      dispatch ? addDaysToIsoDate(dispatch, -3) : null,
      "正式提醒",
      tier === "manual",
      "strong",
    ),
    mkStage(
      "escalate_l1",
      gs.notify_escalate_l1_date,
      dispatch ? addDaysToIsoDate(dispatch, -1) : null,
      "最後提醒",
      tier === "manual",
      "urgent",
    ),
    mkStage("dispatch", gs.dispatch_lower_date, depletionLow, "建議出車", false, "urgent"),
  ].filter(Boolean) as NotifyStageItem[];

  if (mockFields.includes("dispatch_lower_date")) {
    mockFields.push("stock_status_derived");
  }

  const uniqueMockFields = Array.from(new Set(mockFields));
  const daysToDispatch = daysUntil(dispatch);
  const daysToDepletionWindow = daysUntil(depletionLow);

  const notifyStandardDate = notifyStages.find((s) => s.key === "standard")?.date ?? null;
  const daysToNotify = daysUntil(notifyStandardDate);
  const reminderStage: ReminderStage =
    daysToDepletionWindow !== null && daysToDepletionWindow <= 0
      ? "in_window"
      : daysToNotify !== null && daysToNotify <= 0
        ? "due"
        : "before";

  const stockStatus = stockStatusFromDaysToDispatch(daysToDispatch);
  const phase = phaseFromDates(dispatch, depletionLow, depletionHigh);
  const theme = STATUS_THEME[stockStatus];

  const span = daysBetween(gs.last_order_date, depletionHigh);
  const cycleSpanDays = Math.max(1, span ?? 1);
  const elapsed = daysBetween(gs.last_order_date, todayIso());
  const cycleProgressPercent = clampPct(((elapsed ?? 0) / cycleSpanDays) * 100);
  const intervalTimelineMode = pickTimelineMode(cycleSpanDays, daysToDepletionWindow, phase);
  const intervalCaption = buildIntervalCaption(intervalTimelineMode, daysToDepletionWindow, cycleProgressPercent, phase);

  const heroContextLine =
    reminderStage === "in_window"
      ? "建議立即預約"
      : reminderStage === "due"
        ? "時間還充裕，建議先預約"
        : intervalTimelineMode === "summary"
          ? intervalCaption
          : dispatch
            ? `建議最晚於 ${toMonthDay(dispatch)} 前安排配送`
            : intervalCaption;
  const showDispatchHint =
    intervalTimelineMode !== "summary" ||
    phase === "in_window" ||
    phase === "near_lower" ||
    phase === "urgent" ||
    (daysToDispatch !== null && daysToDispatch <= 45);

  const today = todayIso();
  let timelineRangeStart = gs.last_order_date;
  let timelineRangeEnd = depletionHigh;
  if (intervalTimelineMode === "focus") {
    timelineRangeStart = addDaysToIsoDate(depletionLow, -21) ?? addDaysToIsoDate(today, -14) ?? gs.last_order_date;
    if (timelineRangeStart < gs.last_order_date) timelineRangeStart = gs.last_order_date;
    timelineRangeEnd = addDaysToIsoDate(depletionHigh, 7) ?? depletionHigh;
  }

  return {
    depletionLow,
    depletionHigh,
    dispatchByDate: dispatch,
    observeUpperDate,
    lastOrderDate: gs.last_order_date,
    stockStatus,
    phase,
    daysToDispatch,
    statusSubtitle: buildStatusSubtitle(stockStatus, daysToDispatch),
    depletionRangeLabel: formatDepletionRange(depletionLow, depletionHigh),
    dispatchHint: dispatch ? `建議最晚於 ${toMonthDay(dispatch)} 前安排配送` : "請留意用量並適時預約",
    cycleSpanDays,
    cycleProgressPercent,
    daysToDepletionWindow,
    intervalTimelineMode,
    intervalCaption,
    showDispatchHint,
    reminderStage,
    heroContextLine,
    todayStatusLabel: stockStatus,
    timelineRangeStart,
    timelineRangeEnd,
    notifyStages,
    activeReminder: pickActiveReminder(reminderStage, notifyStandardDate, dispatch, tier),
    // 資料層仍標 auto；是否顯示「自動安排中」由 HomePage 暖啟動開關覆寫
    showAutoDispatchMessage: tier === "auto",
    tankFillPercent: theme.tank,
    statusColor: theme.color,
    heroGradient: theme.gradient,
    mockFields: uniqueMockFields,
    hasMockFields: uniqueMockFields.length > 0,
    depletionIntervalIsMock: uniqueMockFields.includes("depletion_interval"),
    dispatchHintIsMock: uniqueMockFields.includes("dispatch_lower_date"),
    reminderIsMock: uniqueMockFields.includes("notify_stages") || uniqueMockFields.includes("notify_tier"),
  };
};

export const clampPct = (n: number) => Math.min(100, Math.max(0, n));

export const dateToPct = (date: string, start: string, end: string): number => {
  const s = new Date(`${start}T00:00:00+08:00`).getTime();
  const e = new Date(`${end}T00:00:00+08:00`).getTime();
  const d = new Date(`${date}T00:00:00+08:00`).getTime();
  if (e <= s) return 50;
  return clampPct(((d - s) / (e - s)) * 100);
};

export const getTodayIso = todayIso;
